import { Fragment, useState } from "react";
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { MenuItem, Skeleton, TableFooter, TablePagination, TableSortLabel, Tooltip } from "@mui/material";
import { visuallyHidden } from '@mui/utils';

interface TherapistTableData {
  name: string,
  // lastName: string | undefined,
  // nickName: string | undefined,
  value: number, // Umsatz
  auslastung: number,
  revenueProductivityHour: number,
  revenueScheduledHour: number,
  rxCount: number,
  gaps: number,
  gapsInHours: number,
}

interface InnerHeadCells {
  id: keyof TherapistTableData,
  label: string,
  align?: "left" | "center" | "right",
}

const innerHeadCells: readonly InnerHeadCells[] = [
  {id: "name", label: "Name", align: "left"},
  {id: "value", label: "Umsatz", align: "right"},
  {id: "auslastung", label: "Auslastung", align: "right"},
  {id: "revenueProductivityHour", label: "Umsatz pro belegte Stunde", align: "right"},
  {id: "revenueScheduledHour", label: "Umsatz pro Arbeitsstunde", align: "right"},
  {id: "rxCount", label: "Rx", align: "right"},
  {id: "gaps", label: "Lücken", align: "right"},
]

interface OuterHeadCells {
  id: string,
  label: string,
  align?: "left" | "center" | "right",
}

const outerHeadCells: OuterHeadCells[] = [
  { id: "therapists", label: "Therapeuten", align: "right" },
  { id: "value", label: "Umsatz", align: "right" },
  { id: "auslastung", label: "Auslastung", align: "right" },
  { id: "rx", label: "Rx", align: "right" },
  { id: "gaps", label: "Lücken", align: "right" }
]

interface TherapistAverageData {
  locationId: string,
  aveValue: number,
  aveAuslastung: number,
  aveRevenueProductivityHour: number,
  aveRevenueScheduledHour: number,
  aveRxCount: number,
  aveGaps: number,
  aveGapsInHours: number,
}

interface TherapistData {
  locationId: string,
  totalTherapistCount: number,
  totalValue: number,
  totalAuslastung: number,
  totalRxCount: number,
  totalGaps: number,
  totalGapsInHours: number,
  table: TherapistTableData[],
  average: TherapistAverageData[],
}

interface TableProps {
  therapistData: TherapistData[],
  isLoading: boolean,
}

const TherapistCollapsibleTable: React.FC<TableProps> = ({ therapistData, isLoading }) => {
  const [open, setOpen] = useState<{[key: string]: boolean}>({});
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof TherapistTableData>('value');

   // Function to handle sorting
  const handleSort = (key: keyof TherapistTableData) => {
    if (orderBy === key) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(key);
      setOrder("asc");
    }
  };

  const sortingFunction = (a: TherapistTableData, b: TherapistTableData) => {
    const aVal = a[orderBy];
    const bVal = b[orderBy];
  
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    } else if (typeof aVal === 'string' && typeof bVal === 'string') {
      return order === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    return 0;
  }

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  
  return (
    <TableContainer className="therapist-table" component={Paper} elevation={0} sx={{ mt: 2, backgroundColor: 'white', minWidth: "100%", height: "100%" }}>
      <Table aria-label="collapsible table">
        <TableHead className="outer-header">
          <TableRow>
            <TableCell />
            <TableCell />
            {outerHeadCells.map((headCell) => (
              <TableCell key={headCell.id} align={headCell.align}>
                {headCell.id === 'gaps' ? (
                  <Tooltip title="in Stunden" arrow placement="top">
                    <span>{headCell.label}</span>
                  </Tooltip>
                ) : headCell.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell>
                <IconButton aria-label="expand row" size="small">
                  <KeyboardArrowDownIcon />
                </IconButton>
              </TableCell>
              <TableCell />
              {outerHeadCells.map((headCell) => (
                <TableCell key={headCell.id}>
                  <Skeleton animation="wave" variant="text" width="100%" />
                </TableCell>
              ))}
            </TableRow>
          ) : therapistData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">  
                  Keine Daten
                </TableCell>
              </TableRow>
          ) :
            therapistData.map((row) => (
              <Fragment key={row.locationId}>
                <TableRow className="collapsed-row" sx={{ '& > *': { borderBottom: 'unset' } }}>
                  <TableCell>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => setOpen((prevState) => ({
                        ...prevState,
                        [row.locationId]: !prevState[row.locationId]
                      }))}
                    >
                      {open[row.locationId] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell component="th" scope="row">{row.locationId}</TableCell>
                  <TableCell align="right">{row.totalTherapistCount}</TableCell>
                  <TableCell align="right">{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(row.totalValue)}</TableCell>
                  <TableCell align="right">{new Intl.NumberFormat("de-DE", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.totalAuslastung)}</TableCell>
                  <TableCell align="right">{row.totalRxCount}</TableCell>
                  <TableCell align="right">{new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(row.totalGapsInHours)}</TableCell>
                </TableRow>
  
                {/* table for each location */}
                <TableRow>
                  <TableCell sx={{ paddingBottom: 0, paddingTop: 1 }} colSpan={7}>
                    <Collapse in={open[row.locationId]} timeout="auto" unmountOnExit>
                      <Box sx={{ padding: 1 }}>
                        <Table size="small" aria-label="therapists">
                          <TableHead>
                            <TableRow className="inner-header">
                              {innerHeadCells.map((headCell) => (
                                <TableCell
                                  key={headCell.id}
                                  align="center"
                                  padding="none"
                                  sortDirection={orderBy === headCell.id ? order : false}
                                >
                                  <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={orderBy === headCell.id ? order : 'asc'}
                                    onClick={() => handleSort(headCell.id)}
                                  >
                                    {headCell.id === 'gaps' ? (
                                      <Tooltip title="in Stunden" arrow placement="top">
                                        <span>{headCell.label}</span>
                                      </Tooltip>
                                    ) : headCell.label}
                                    {orderBy === headCell.id && (
                                      <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                      </Box>
                                    )}
                                  </TableSortLabel>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...row.table].sort(sortingFunction)
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((tableRow) => (
                              <TableRow className="inner-row" hover key={tableRow.name}>
                                <TableCell>{tableRow.name}</TableCell>
                                <TableCell align="center">{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(tableRow.value)}</TableCell>
                                <TableCell align="center">{new Intl.NumberFormat("de-DE", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(tableRow.auslastung)}</TableCell>
                                <TableCell align="center">{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(tableRow.revenueProductivityHour)}</TableCell>
                                <TableCell align="center">{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(tableRow.revenueScheduledHour)}</TableCell>
                                <TableCell align="center">{tableRow.rxCount}</TableCell>
                                <TableCell align="center">{new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(tableRow.gapsInHours)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                          {[...row.average].map((aveRow) => (
                            <TableRow>
                              <TableCell align="center" sx={{ fontWeight: 700, pt: 1, pb: 1, borderTop: "1.5px solid black" }}>Durchschnitt</TableCell>
                              <TableCell align="center" sx ={{ pt: 1, pb: 1, borderTop: "1.5px solid black" }}>{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(aveRow.aveValue)}</TableCell>
                              <TableCell align="center" sx ={{ pt: 1, pb: 1, borderTop: "1.5px solid black" }}>{new Intl.NumberFormat("de-DE", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(aveRow.aveAuslastung)}</TableCell>
                              <TableCell align="center" sx ={{ pt: 1, pb: 1, borderTop: "1.5px solid black" }}>{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(aveRow.aveRevenueProductivityHour)}</TableCell>
                              <TableCell align="center" sx ={{ pt: 1, pb: 1, borderTop: "1.5px solid black" }}>{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(aveRow.aveRevenueScheduledHour)}</TableCell>
                              <TableCell align="center" sx ={{ pt: 1, pb: 1, borderTop: "1.5px solid black" }}>{Math.round(aveRow.aveRxCount)}</TableCell>
                              <TableCell align="center" sx ={{ pt: 1, pb: 1, borderTop: "1.5px solid black" }}>{new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(aveRow.aveGapsInHours)}</TableCell>
                            </TableRow>
                          ))}
                        </Table>  
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: 'flex-end' }}>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 20, 30]}
                            count={[...row.table].length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Zeilen pro Seite"
                            labelDisplayedRows={({ from, to, count }) =>
                              `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
                            }
                            sx={{
                              fontSize: 11,
                              fontFamily: "Inter",
                              borderBottom: "none",
                              '& .MuiTablePagination-toolbar': {
                                fontSize: 11,
                                fontFamily: "Inter",
                                minHeight: 30,
                              },
                              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: 11,
                                fontFamily: "Inter",
                              },
                              '& .MuiInputBase-root': {
                                fontSize: 11,
                                fontFamily: "Inter",
                              },
                              '& .MuiTablePagination-select': {
                                fontSize: 11,
                                fontFamily: "Inter",
                                paddingTop: 0.5,
                                paddingBottom: 0.5,
                              },
                              '& svg': {
                                fontSize: 18,
                              },
                            }}
                            slotProps={{
                              select: {
                                MenuProps: {
                                  MenuListProps: {
                                    sx: {
                                      '& .MuiMenuItem-root': {
                                        fontSize: 11,
                                        fontFamily: 'Inter',
                                      },
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </TableRow>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TherapistCollapsibleTable;